// src/utils/kpi.ts
export const H = 3_600_000;        // 1 час
export const D = 24 * H;           // 1 день
export const W = 7 * D;            // неделя (для удобства)
export const M = 30 * D;           // месяц (условно 30 дней)
export const Y = 365 * D;          // год (условно 365 дней)

export type Window = { curr: number; prev: number };

/** Разница в процентах с префиксом +/-, как в UI */
export function pct(curr: number, prev: number): string {
  if (prev === 0 && curr === 0) return '+0%';
  if (prev === 0 && curr > 0) return '+100%';
  const v = Math.round(((curr - prev) / prev) * 100);
  return `${v >= 0 ? '+' : ''}${v}%`;
}

/** Безопасно получить timestamp; если дата невалидна — вернёт NaN (запись просто будет проигнорирована) */
const ts = (isoOrDate: string | Date): number => {
  const t = isoOrDate instanceof Date ? isoOrDate.getTime() : new Date(isoOrDate).getTime();
  return Number.isFinite(t) ? t : NaN;
};

/** Текущее/предыдущее окно по сумме значений (по умолчанию берём .total) */
export function currPrevSum<T extends { createdAt: string }>(
  items: T[],
  periodMs: number,
  getVal: (it: T) => number = (it: any) => it.total ?? 0
): Window {
  const now = Date.now();
  const currFrom = now - periodMs;
  const prevFrom = now - 2 * periodMs;

  const sumBetween = (fromMs: number, toMs: number) =>
    (items ?? []).reduce((s, it) => {
      const t = ts(it.createdAt);
      return t >= fromMs && t < toMs ? s + (getVal(it) || 0) : s;
    }, 0);

  return {
    curr: sumBetween(currFrom, now),
    prev: sumBetween(prevFrom, currFrom),
  };
}

/** Текущее/предыдущее окно по количеству записей (документов) */
export function currPrevCount<T extends { createdAt: string }>(
  items: T[],
  periodMs: number
): Window {
  const now = Date.now();
  const currFrom = now - periodMs;
  const prevFrom = now - 2 * periodMs;

  const countBetween = (fromMs: number, toMs: number) =>
    (items ?? []).reduce((s, it) => {
      const t = ts(it.createdAt);
      return t >= fromMs && t < toMs ? s + 1 : s;
    }, 0);

  return {
    curr: countBetween(currFrom, now),
    prev: countBetween(prevFrom, currFrom),
  };
}

/** Текущее/предыдущее окно по количеству позиций (строк) внутри сущности.
 * Если в записи нет позиций — считаем её как 1 строку.
 */
export function currPrevLines<T extends { createdAt: string; items?: any[] }>(
  items: T[],
  periodMs: number
): Window {
  const now = Date.now();
  const currFrom = now - periodMs;
  const prevFrom = now - 2 * periodMs;

  const countLines = (o: T) => {
    const lines = Array.isArray(o?.items) ? o.items : [];
    return lines.length || 1;
  };

  const countBetween = (fromMs: number, toMs: number) =>
    (items ?? []).reduce((s, o) => {
      const t = ts(o.createdAt);
      return t >= fromMs && t < toMs ? s + countLines(o) : s;
      // невалидные даты автоматически отсекаются
    }, 0);

  return {
    curr: countBetween(currFrom, now),
    prev: countBetween(prevFrom, currFrom),
  };
}
